import React, {useEffect, useState} from "react";
import {FiEye, FiEdit2, FiRotateCcw, FiUpload, FiX, FiEyeOff} from "react-icons/fi";
import API from "../api/config";
import toast from "react-hot-toast";
import imageCompression from "browser-image-compression";
import Logo from "../assets/QRscriptsLogo1.png";

/* ---------- helpers ---------- */
const fileToBase64 = (file) =>
    new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
    });

const normalizeImageSrc = (src) => {
    if (!src || typeof src !== "string") return "";
    if (src.startsWith("data:image")) return src;
    return `data:image/jpeg;base64,${src}`;
};

const EditRegistration = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    // Form States
    const [owner, setOwner] = useState("");
    const [callme, setCallme] = useState("");
    const [mode, setMode] = useState("url");
    const [lockedMode, setLockedMode] = useState(null);
    const [url, setUrl] = useState("");
    const [boxNote, setBoxNote] = useState("");
    const [images, setImages] = useState([]);
    const [previewing, setPreviewing] = useState(null);
    const [search, setSearch] = useState("");
    const [editAccessType, setEditAccessType] = useState("public");
    const [editPassword, setEditPassword] = useState("");
    const [showEditPassword, setShowEditPassword] = useState(false);
    // const previousAccessType = editing?.access_type || "public";

    /* ---------- fetch ---------- */
    const fetchData = async () => {
        try {
            const email = localStorage.getItem("verified_email");
            if (!email) {
                toast.error("Session expired. Please login again.");
                return;
            }

            const res = await API.get("/registrations/my", {
                headers: {"x-user-email": email},
            });

            setRows(res.data || []);
            // setRows(res.data.registrations || []);
        } catch {
            toast.error("Failed to load registrations");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    /* ---------- reset ---------- */
    const handleDeleteSingleQr = async (qrId) => {
        if (!window.confirm(`Reset QR ${qrId}? This will delete all data.`)) return;
        try {
            await API.delete(`/registrations/reset/${qrId}`);
            toast.success("QR reset successfully");
            setRows((prev) => prev.filter((r) => r.qr_id !== qrId));
        } catch {
            toast.error("Failed to reset QR");
        }
    };

    /* ---------- open edit ---------- */
    const openEdit = async (row) => {
        try {
            setEditing(null);
            setImages([]);
            setUrl("");
            setBoxNote("");
            setOwner("");
            setCallme("");
            setEditing(row);
            setEditAccessType(row.access_type || "public");
            setEditPassword("");
            setOwner(row.owner_name || "");
            setCallme(row.callme || "");

            // CHANGE: Compare against "Photos" instead of "IMAGE"
            const currentFormat = row.format;

            if (currentFormat === "Photos") {
                setMode("images");
                setLockedMode("images");

                const res = await API.get(`/registrations/gallery/${row.qr_id}`);
                const rawImages = Array.isArray(res.data.images)
                    ? res.data.images
                    : JSON.parse(res.data.images || "[]");

                setImages(rawImages.map((src) => ({src, existing: true})));
                setBoxNote(res.data.box_note || "");
            } else {
                setMode("url");
                setLockedMode("url");
                setUrl(row.url || "");
            }
        } catch (err) {
            console.error("Error opening edit modal:", err);
            toast.error("Failed to load details");
        }
    };

    /* ---------- image handling ---------- */
    const compressImageToBase64 = async (file) => {
        const options = {
            maxSizeMB: 0.3, // ≈ 300 KB
            maxWidthOrHeight: 1280, // WhatsApp-like resize
            useWebWorker: true,
            initialQuality: 0.7,
        };

        const compressedFile = await imageCompression(file, options);

        return await fileToBase64(compressedFile);
    };

    const onFiles = async (fileList) => {
        const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));

        if (files.length + images.length > 5) {
            toast.error("Maximum 5 images allowed");
            return;
        }

        try {
            const compressedBase64s = await Promise.all(files.map((file) => compressImageToBase64(file)));

            const newImages = compressedBase64s.map((src) => ({
                src,
                existing: false, // ✅ new image
            }));

            setImages((prev) => [...prev, ...newImages]);
        } catch (err) {
            console.error("Image compression failed:", err);
            toast.error("Failed to process image");
        }
    };

    const removeImage = (index) => {
        setImages((prev) => prev.filter((_, i) => i !== index));
    };

    /* ---------- save ---------- */
    const saveEdit = async () => {
        try {
            if (!editing) return; // safety

            const previousAccessType = editing?.access_type || "public";

            if (mode === "images") {
                if (images.length === 0) return toast.error("At least 1 image is required");
                if (images.length > 5) return toast.error("Maximum 5 images allowed");
            }

            const payload = {
                owner_name: owner,
                callme,
                box_note: mode === "images" ? boxNote : null,
            };

            // ➕ ACCESS TYPE
            payload.access_type = editAccessType;

            // ➕ PASSWORD (only if entered)
            if (editAccessType === "private" && editPassword.trim() !== "") {
                payload.password = editPassword;
            }

            if (mode === "url") payload.url = url;

            await API.put(`/registrations/${editing.qr_id}`, payload);

            if (mode === "images") {
                const allImageSources = images.map((i) => i.src);
                await API.put(`/registrations/images/${editing.qr_id}`, {
                    images: allImageSources,
                });
            }

            // Reset
            setEditing(null);
            setOwner("");
            setCallme("");
            setUrl("");
            setBoxNote("");
            setImages([]);
            setLockedMode(null);

            await fetchData();

            // 🔔 Smart Toast
            if (previousAccessType === "public" && editAccessType === "private") {
                toast.success("QR updated: Now Private 🔐");
            } else if (previousAccessType === "private" && editAccessType === "public") {
                toast.success("QR updated: Now Public 🌍");
            } else if (editAccessType === "private" && editPassword.trim() !== "") {
                toast.success("Password updated successfully 🔑");
            } else {
                toast.success("Updated successfully");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Update failed");
        }
    };

    const filteredRows = rows.filter((r) => {
        const q = search.toLowerCase();
        return (
            r.qr_id?.toLowerCase().includes(q) ||
            r.owner_name?.toLowerCase().includes(q) ||
            r.callme?.toLowerCase().includes(q) ||
            r.url?.toLowerCase().includes(q) ||
            r.format?.toLowerCase().includes(q) // Added format search support
        );
    });

    if (loading)
        return <div className="flex justify-center items-center h-screen text-slate-500">Loading your data...</div>;

    return (
        <>
            {/* ================= HEADER ================= */}
            <div className="bg-white border-b border-pink-100 shadow-sm relative overflow-hidden">
                {/* Sublte background glow to match the login page vibe */}
                <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-pink-50 rounded-full blur-3xl opacity-50"></div>

                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between relative z-10">
                    {/* LEFT: Logo + Brand */}
                    <div className="flex items-center gap-4">
                        <a
                            href="https://www.qrscripts.in/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block hover:opacity-80 transition-opacity"
                        >
                            <div className="bg-pink-100 p-2 rounded-xl cursor-pointer">
                                <img src={Logo} alt="QR Scripts" className="h-10 w-auto" />
                            </div>
                        </a>
                        <div>
                            <h1 className="bg-gradient-to-r from-pink-600 via-pink-500 to-purple-700 bg-clip-text text-transparent font-extrabold text-xl leading-tight">
                                QR Scripts
                            </h1>
                            <p className="text-slate-500 text-[10px] uppercase tracking-wider font-bold">
                                Management Dashboard
                            </p>
                        </div>
                    </div>

                    {/* RIGHT: User Info (Hidden on mobile) */}
                    <div className="text-right hidden sm:block">
                        <div className="flex items-center gap-3 justify-end">
                            <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
                            <p className="text-slate-900 text-sm font-bold">
                                Welcome,{" "}
                                <span className="text-pink-600 italic font-medium">
                                    {localStorage.getItem("verified_email")}
                                </span>
                            </p>
                        </div>

                        <p className="text-slate-400 text-[10px] font-semibold mt-0.5 uppercase tracking-tighter">
                            {rows.length} Active QR Scripts
                        </p>
                    </div>
                </div>
            </div>

            {/* ================= PAGE CONTENT ================= */}
            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* Search + count */}
                <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search QR ID, Owner, Unique or URL"
                        className="w-full md:w-80 border border-slate-300 rounded-xl px-4 py-2.5
                     text-sm focus:ring-2 focus:ring-pink-500 outline-none"
                    />
                    <div>
                        <p className="text-sm text-slate-500">
                            {filteredRows.length} of {rows.length} shown
                        </p>
                    </div>
                </div>

                {/* ======= TABLE (unchanged) ======= */}
                {/* keep your table + modals exactly as-is below */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    {/* DESKTOP TABLE */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-4 text-left font-semibold text-slate-700">QR ID</th>
                                    <th className="px-6 py-4 text-left font-semibold text-slate-700">Type</th>
                                    <th className="px-6 py-4 text-left font-semibold text-slate-700">URL / Content</th>
                                    <th className="px-6 py-4 text-left font-semibold text-slate-700">Owner</th>
                                    <th className="px-6 py-4 text-left font-semibold text-slate-700">Unique</th>
                                    <th className="px-6 py-4 text-right font-semibold text-slate-700">Access</th>
                                    <th className="px-6 py-4 text-right font-semibold text-slate-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredRows.map((r) => (
                                    <tr key={r.qr_id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4 font-mono text-pink-600">{r.qr_id}</td>
                                        <td className="px-6 py-4">{r.format}</td>
                                        <td className="px-6 py-4 truncate max-w-[200px]">
                                            {r.url || "Gallery Content"}
                                        </td>
                                        <td className="px-6 py-4">{r.owner_name || "—"}</td>
                                        <td className="px-6 py-4">{r.callme || "—"}</td>
                                        <td className="px-6 py-4 text-right">
                                            <span
                                                className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                                                    r.access_type === "private"
                                                        ? "bg-red-100 text-red-700"
                                                        : "bg-green-100 text-green-700"
                                                }`}
                                            >
                                                {r.access_type || "Public"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex gap-3 justify-end">
                                                <button onClick={() => setPreviewing(r)}>
                                                    <FiEye />
                                                </button>
                                                <button onClick={() => openEdit(r)}>
                                                    <FiEdit2 />
                                                </button>
                                                <button onClick={() => handleDeleteSingleQr(r.qr_id)}>
                                                    <FiRotateCcw />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* MOBILE CARDS */}
                    <div className="md:hidden divide-y divide-slate-100">
                        {filteredRows.map((r) => (
                            <div key={r.qr_id} className="p-4 space-y-2">
                                <div className="flex justify-between items-center">
                                    <span className="font-mono text-pink-600 font-semibold">{r.qr_id}</span>
                                    <span
                                        className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                                            r.access_type === "private"
                                                ? "bg-red-100 text-red-700"
                                                : "bg-green-100 text-green-700"
                                        }`}
                                    >
                                        {r.access_type || "Public"}
                                    </span>
                                </div>

                                <div className="text-xs text-slate-500 uppercase">{r.format}</div>

                                <div className="text-sm text-slate-700 break-all">{r.url || "Gallery Content"}</div>

                                <div className="text-sm">
                                    <span className="font-semibold">Owner:</span> {r.owner_name || "—"}
                                </div>

                                <div className="text-sm">
                                    <span className="font-semibold">Unique:</span> {r.callme || "—"}
                                </div>

                                <div className="flex gap-4 pt-2">
                                    <button onClick={() => setPreviewing(r)}>
                                        <FiEye />
                                    </button>
                                    <button onClick={() => openEdit(r)}>
                                        <FiEdit2 />
                                    </button>
                                    <button onClick={() => handleDeleteSingleQr(r.qr_id)}>
                                        <FiRotateCcw />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* EDIT MODAL */}
                {editing && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
                            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                                <h2 className="font-bold text-slate-800">
                                    Edit QR Data <span className="text-pink-600 ml-1">#{editing.qr_id}</span>
                                </h2>
                                <button
                                    onClick={() => setEditing(null)}
                                    className="text-slate-400 hover:text-slate-600 text-xl"
                                >
                                    <FiX />
                                </button>
                            </div>

                            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                                <div className="bg-blue-50 border border-blue-100 p-3 rounded-lg text-xs text-blue-700">
                                    This QR is permanently set to{" "}
                                    <strong>{lockedMode === "images" ? "PHOTOS" : "LINKS"}</strong> mode.
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                        Owner Name
                                    </label>
                                    <input
                                        value={owner}
                                        onChange={(e) => setOwner(e.target.value)}
                                        className="w-full border border-slate-200 rounded-lg p-2.5 focus:ring-2 focus:ring-pink-500 outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                        Unique (CallMe)
                                    </label>
                                    <input
                                        value={callme}
                                        onChange={(e) => setCallme(e.target.value)}
                                        className="w-full border border-slate-200 rounded-lg p-2.5 focus:ring-2 focus:ring-pink-500 outline-none"
                                        placeholder="Unique identifier"
                                    />
                                </div>
                                {mode === "url" ? (
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                            Redirect URL
                                        </label>
                                        <input
                                            value={url}
                                            onChange={(e) => setUrl(e.target.value)}
                                            className="w-full border border-slate-200 rounded-lg p-2.5 focus:ring-2 focus:ring-pink-500 outline-none"
                                            placeholder="https://example.com"
                                        />
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                                                Gallery Images ({images.length}/5)
                                            </label>

                                            {/* Upload Box - Only if < 5 images */}
                                            {images.length < 5 && (
                                                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-slate-300 border-dashed rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors">
                                                    <div className="flex flex-col items-center justify-center pt-2">
                                                        <FiUpload className="text-slate-400 w-6 h-6 mb-1" />
                                                        <p className="text-[10px] text-slate-500 font-semibold uppercase">
                                                            Add Images
                                                        </p>
                                                    </div>
                                                    <input
                                                        type="file"
                                                        multiple
                                                        accept="image/*"
                                                        onChange={(e) => onFiles(e.target.files)}
                                                        className="hidden"
                                                    />
                                                </label>
                                            )}
                                        </div>

                                        {/* Image Preview Grid */}
                                        <div className="grid grid-cols-3 gap-3">
                                            {images.map((img, i) => (
                                                <div key={i} className="relative aspect-square group">
                                                    <img
                                                        src={normalizeImageSrc(img.src)}
                                                        className="h-full w-full object-cover rounded-lg border border-slate-200 shadow-sm"
                                                        alt=""
                                                    />
                                                    <button
                                                        onClick={() => removeImage(i)}
                                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                                                    >
                                                        <FiX size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                                Box Note (Photo Context)
                                            </label>
                                            <textarea
                                                value={boxNote}
                                                onChange={(e) => setBoxNote(e.target.value)}
                                                rows="2"
                                                className="w-full border border-slate-200 rounded-lg p-2.5 focus:ring-2 focus:ring-pink-500 outline-none text-sm"
                                                placeholder="What's in this box?"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* 🔐 Access Type */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                                        Access Type
                                    </label>

                                    <div className="flex gap-2 mb-3">
                                        <button
                                            type="button"
                                            onClick={() => setEditAccessType("public")}
                                            className={`flex-1 py-2 rounded-lg border ${
                                                editAccessType === "public" ? "bg-black text-white" : ""
                                            }`}
                                        >
                                            Public
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => setEditAccessType("private")}
                                            className={`flex-1 py-2 rounded-lg border ${
                                                editAccessType === "private" ? "bg-black text-white" : ""
                                            }`}
                                        >
                                            Private
                                        </button>
                                    </div>

                                    {editAccessType === "private" && (
                                        <div className="relative">
                                            <input
                                                type={showEditPassword ? "text" : "password"}
                                                value={editPassword}
                                                onChange={(e) => setEditPassword(e.target.value)}
                                                placeholder="Set new passwords"
                                                className="w-full p-2.5 border border-slate-200 rounded-lg pr-10 focus:ring-2 focus:ring-pink-500 outline-none"
                                            />

                                            <button
                                                type="button"
                                                onClick={() => setShowEditPassword(!showEditPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                                            >
                                                {showEditPassword ? <FiEyeOff /> : <FiEye />}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex gap-3">
                                <button
                                    onClick={() => setEditing(null)}
                                    className="flex-1 px-4 py-2.5 bg-white border border-slate-200 text-slate-600 font-semibold rounded-lg hover:bg-slate-100 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={saveEdit}
                                    className="flex-1 px-4 py-2.5 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 shadow-md shadow-pink-200 transition-all"
                                >
                                    Update Settings
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* PREVIEW MODAL - READ ONLY */}
                {previewing && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden relative animate-in fade-in zoom-in duration-200">
                            {/* Close Button */}
                            <button
                                onClick={() => setPreviewing(null)}
                                className="absolute top-4 right-4 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-full p-2 z-10"
                            >
                                <FiX size={20} />
                            </button>

                            <div className="p-8">
                                <div className="mb-6">
                                    <h2 className="text-xl font-bold text-slate-800">Live Preview</h2>
                                    <p className="text-sm text-slate-500 font-mono">ID: {previewing.qr_id}</p>
                                </div>

                                {/* {previewing.format === "URL" ? ( */}
                                {previewing.format === "Links" ? (
                                    /* URL PREVIEW MODE */
                                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-10 text-center">
                                        <p className="text-slate-600 mb-4 font-medium">
                                            Scanning this QR redirects to:
                                        </p>
                                        <a
                                            href={previewing.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 font-bold text-lg underline break-all block hover:text-blue-700"
                                        >
                                            {previewing.url || "No URL provided"}
                                        </a>
                                        <p className="mt-4 text-xs text-slate-400">
                                            Click the link above to test the destination.
                                        </p>
                                    </div>
                                ) : (
                                    /* IMAGE PREVIEW MODE - NO EDIT/DELETE OPTIONS */
                                    <div className="space-y-6">
                                        <p className="text-slate-600 font-semibold uppercase text-xs tracking-wider">
                                            Image Gallery Content
                                        </p>

                                        <div className="grid grid-cols-2 gap-4 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                                            {previewing.images ? (
                                                (() => {
                                                    try {
                                                        const parsed =
                                                            typeof previewing.images === "string"
                                                                ? JSON.parse(previewing.images)
                                                                : previewing.images;

                                                        return parsed.map((img, idx) => (
                                                            <div key={idx} className="relative aspect-square">
                                                                <img
                                                                    src={normalizeImageSrc(img)}
                                                                    alt="Preview"
                                                                    className="rounded-xl w-full h-full object-cover shadow-sm border border-slate-100"
                                                                />
                                                            </div>
                                                        ));
                                                    } catch (e) {
                                                        return (
                                                            <p className="col-span-2 text-center text-slate-400">
                                                                Invalid gallery data
                                                            </p>
                                                        );
                                                    }
                                                })()
                                            ) : (
                                                <p className="col-span-2 text-center text-slate-400 py-10">
                                                    No images found in this gallery.
                                                </p>
                                            )}
                                        </div>

                                        {previewing.box_note && (
                                            <div className="bg-slate-50 border-l-4 border-slate-400 text-slate-700 p-4 rounded-r-lg text-sm">
                                                <span className="font-bold block mb-1">Box Note:</span>
                                                {previewing.box_note}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="mt-8 flex justify-end">
                                    <button
                                        onClick={() => setPreviewing(null)}
                                        className="px-8 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg"
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default EditRegistration;
